import { combineClinicAndPractitionerPeriods, usableHourPeriods } from '@/lib/clinicHours';
import { resolveAppointmentStatus } from '@/utils/appointmentStatus';
import { clinicMinutesFromMidnight } from '@/utils/calendarClinicTime';

export type CalendarVisitStatus =
  | 'checked_in'
  | 'under_treatment'
  | 'treatment_finished'
  | 'payment_completed'
  | null;

export type CalendarAppointmentStatus = 'upcoming' | 'completed' | 'cancelled' | 'no_show';

/** Clinical/booking lifecycle. Not shown as a calendar icon. */
export type CalendarLifecycleStatus =
  | 'upcoming'
  | 'checked_in'
  | 'under_treatment'
  | 'treatment_finished'
  | 'completed'
  | 'no_show'
  | 'cancelled';

export const DEFAULT_SERVICE_COLOR = 'var(--muted)';

/** @deprecated Status no longer colours calendar blocks; kept for older imports. */
export const CALENDAR_STATUS_COLORS = {
  upcoming: 'var(--teal)',
  checked_in: 'var(--teal)',
  under_treatment: 'var(--coral)',
  treatment_finished: 'var(--green)',
  payment_completed: 'var(--teal)',
  completed: 'var(--green)',
  no_show: 'var(--coral)',
  cancelled: 'var(--line-strong)',
} as const;

export type CalendarStatusKey = CalendarLifecycleStatus | 'payment_completed' | 'completed';

function isCssColor(value: string): boolean {
  return (
    /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(value) ||
    /^rgba?\(/i.test(value) ||
    /^hsla?\(/i.test(value)
  );
}

export function resolveServiceColor(serviceColor: string | null | undefined): string {
  const trimmed = serviceColor?.trim();
  if (trimmed && isCssColor(trimmed)) return trimmed;
  return DEFAULT_SERVICE_COLOR;
}

function parseRgbChannels(value: string): [number, number, number] | null {
  const hex = value.trim();
  const short = /^#([0-9a-f]{3})$/i.exec(hex);
  if (short) {
    const n = short[1];
    return [parseInt(n[0] + n[0], 16), parseInt(n[1] + n[1], 16), parseInt(n[2] + n[2], 16)];
  }
  const full = /^#([0-9a-f]{6})$/i.exec(hex);
  if (full) {
    const n = full[1];
    return [parseInt(n.slice(0, 2), 16), parseInt(n.slice(2, 4), 16), parseInt(n.slice(4, 6), 16)];
  }
  const rgb = /^rgba?\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)/i.exec(hex);
  if (rgb) return [Number(rgb[1]), Number(rgb[2]), Number(rgb[3])];
  return null;
}

function rgbToHsl(r: number, g: number, b: number): [number, number, number] {
  const rn = r / 255;
  const gn = g / 255;
  const bn = b / 255;
  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  const l = (max + min) / 2;
  if (max === min) return [0, 0, l];
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h = 0;
  if (max === rn) h = ((gn - bn) / d + (gn < bn ? 6 : 0)) / 6;
  else if (max === gn) h = ((bn - rn) / d + 2) / 6;
  else h = ((rn - gn) / d + 4) / 6;
  return [h, s, l];
}

function hueToChannel(p: number, q: number, t: number): number {
  let tt = t;
  if (tt < 0) tt += 1;
  if (tt > 1) tt -= 1;
  if (tt < 1 / 6) return p + (q - p) * 6 * tt;
  if (tt < 1 / 2) return q;
  if (tt < 2 / 3) return p + (q - p) * (2 / 3 - tt) * 6;
  return p;
}

function hslToRgb(h: number, s: number, l: number): [number, number, number] {
  if (s === 0) {
    const v = Math.round(l * 255);
    return [v, v, v];
  }
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  return [
    Math.round(hueToChannel(p, q, h + 1 / 3) * 255),
    Math.round(hueToChannel(p, q, h) * 255),
    Math.round(hueToChannel(p, q, h - 1 / 3) * 255),
  ];
}

function rgbToHex(r: number, g: number, b: number): string {
  const clamp = (n: number) => Math.max(0, Math.min(255, n)).toString(16).padStart(2, '0');
  return `#${clamp(r)}${clamp(g)}${clamp(b)}`;
}

/**
 * Darker/deeper version of a service colour, keeping the same hue.
 * Used for the appointment left rail when the journal is completed.
 */
export function deepenServiceColor(serviceColor: string, amount = 0.35): string {
  const rgb = parseRgbChannels(serviceColor);
  if (!rgb) {
    const pct = Math.round((1 - amount) * 100);
    return `color-mix(in srgb, ${serviceColor} ${pct}%, black)`;
  }
  const [h, s, l] = rgbToHsl(rgb[0], rgb[1], rgb[2]);
  const nextL = Math.max(0.16, l * (1 - amount));
  return rgbToHex(...hslToRgb(h, s, nextL));
}

/** Lightness floor so a stored brand hex (often already dark) is not the default rail. */
const NORMAL_RAIL_MIN_LIGHTNESS = 0.78;

/**
 * The rail colour for a missing / draft journal: the light service colour.
 * Stored `services.color` values are often already a deep brand hex (e.g. #9a244f).
 * Using that hex as the default made every unfinalized card look "completed".
 */
export function normalServiceRailColor(serviceColor: string | null | undefined): string {
  const stored = resolveServiceColor(serviceColor);
  const rgb = parseRgbChannels(stored);
  if (!rgb) {
    return `color-mix(in srgb, ${stored} 38%, white)`;
  }
  const [h, s, l] = rgbToHsl(rgb[0], rgb[1], rgb[2]);
  if (l >= NORMAL_RAIL_MIN_LIGHTNESS) return stored;
  return rgbToHex(...hslToRgb(h, s, NORMAL_RAIL_MIN_LIGHTNESS));
}

export type AppointmentJournalRailKind = 'finalized' | 'draft' | 'cancelled';

/**
 * Left-rail kind for calendar cards and agenda rows.
 * Dark teal only when the journal is finalized. Cancelled is grey and dashed.
 * Service colour never drives the rail (Design system V2).
 */
export function appointmentJournalRailKind(input: {
  status: CalendarAppointmentStatus;
  journalFinalized: boolean;
}): AppointmentJournalRailKind {
  if (input.status === 'cancelled') return 'cancelled';
  return input.journalFinalized === true ? 'finalized' : 'draft';
}

export function appointmentJournalRailColor(kind: AppointmentJournalRailKind): string {
  switch (kind) {
    case 'finalized':
      return 'var(--teal)';
    case 'draft':
      return 'var(--teal-line)';
    case 'cancelled':
      return 'var(--line-strong)';
    default: {
      const _exhaustive: never = kind;
      return _exhaustive;
    }
  }
}

/**
 * Left-rail colour. Dark teal only when `journalFinalized` is explicitly true.
 * Service colour is ignored — the rail is a journal-state token.
 */
export function appointmentServiceRailColor(
  _serviceColor: string | null | undefined,
  journalFinalized: boolean,
): string {
  return appointmentJournalRailColor(
    appointmentJournalRailKind({ status: 'upcoming', journalFinalized }),
  );
}

/**
 * Pastel fill from the service colour. Never keyed off status, practitioner,
 * or service name — only the configured `services.color` value.
 */
export function serviceCalendarColors(serviceColor: string | null | undefined): {
  accent: string;
  fill: string;
} {
  const accent = resolveServiceColor(serviceColor);
  return {
    accent,
    fill: `color-mix(in srgb, ${accent} 22%, var(--card))`,
  };
}

const AA_CONTRAST = 4.5;
const WHITE_RGB: [number, number, number] = [255, 255, 255];

function channelToLinear(channel: number): number {
  const s = channel / 255;
  return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
}

function relativeLuminance(r: number, g: number, b: number): number {
  return 0.2126 * channelToLinear(r) + 0.7152 * channelToLinear(g) + 0.0722 * channelToLinear(b);
}

function contrastRatio(a: [number, number, number], b: [number, number, number]): number {
  const l1 = relativeLuminance(a[0], a[1], a[2]);
  const l2 = relativeLuminance(b[0], b[1], b[2]);
  const lighter = Math.max(l1, l2);
  const darker = Math.min(l1, l2);
  return (lighter + 0.05) / (darker + 0.05);
}

/**
 * Clinic-chosen service / group-session colour as a filled block.
 * White text is used only when it meets AA on that fill; otherwise the colour
 * is an accent rail on `--card` with `--text` (readable in both themes).
 */
export function clinicColorBlockAppearance(serviceColor: string | null | undefined): {
  background: string;
  color: string;
  accent: string;
  accentOnly: boolean;
} {
  const accent = resolveServiceColor(serviceColor);
  const rgb = parseRgbChannels(accent);
  if (rgb && contrastRatio(rgb, WHITE_RGB) >= AA_CONTRAST) {
    return { background: accent, color: '#ffffff', accent, accentOnly: false };
  }
  return {
    background: 'var(--card)',
    color: 'var(--text)',
    accent,
    accentOnly: true,
  };
}

/**
 * Clinical lifecycle from visit/appointment status only.
 * Payment / `visit_status=payment_completed` never finish an appointment.
 */
export function getCalendarLifecycleStatus(input: {
  visit_status: CalendarVisitStatus;
  status: CalendarAppointmentStatus;
  starts_at?: string;
  ends_at?: string;
  now?: Date;
}): CalendarLifecycleStatus {
  return resolveAppointmentStatus({
    status: input.status,
    visitStatus: input.visit_status,
    startsAt: input.starts_at,
    endsAt: input.ends_at,
    now: input.now,
  });
}

export function getAppointmentCalendarAppearance(input: {
  visit_status: CalendarVisitStatus;
  status: CalendarAppointmentStatus;
  payment_status?: string | null;
  service_color?: string | null;
  starts_at?: string;
  ends_at?: string;
  now?: Date;
}): { background: string; accent: string; statusKey: CalendarLifecycleStatus } {
  const { accent, fill } = serviceCalendarColors(input.service_color);
  return {
    background: fill,
    accent,
    statusKey: getCalendarLifecycleStatus(input),
  };
}

export const DEFAULT_DAY_START_HOUR = 0;
export const DEFAULT_DAY_END_HOUR = 24;
/** Fallback open window when no opening or working hours are enabled. */
export const DESIGN_DAY_START_HOUR = 8;
export const DESIGN_DAY_END_HOUR = 17;
/**
 * Extra hour kept above the first open hour on the referral occupancy grid.
 * The appointment calendar does not clip to this pad; it scrolls a full day.
 */
export const VISIBLE_HOURS_PAD = 1;
/** Closed-band fill. Same token as the weekend column — CSS-variable colours cannot use `/70`. */
export const CALENDAR_CLOSED_OVERLAY_CLASS = 'bg-ds-sunk';
export const MIN_HOUR_HEIGHT = 40;
export const MAX_HOUR_HEIGHT = 80;
export const CALENDAR_COLUMN_HEADER_PX = 72;
/**
 * Extra header row when each day is split into practitioner sub-columns.
 * Tall enough for a name to wrap onto two lines (10px, leading-tight) plus the
 * row's top margin. A resource subtitle adds its own line on top of this.
 */
export const CALENDAR_STAFF_HEADER_PX = 34;
/**
 * Narrowest day column that still shows a patient name and a clock range.
 * Seven of these plus the 64px hour gutter still fit a 1280px window with the
 * 248px sidebar, so the usual week view does not scroll. A narrower window
 * scrolls the grid instead of crushing the card to “J…” / “1…”.
 */
export const CALENDAR_DAY_COLUMN_MIN_PX = 120;

/**
 * Split a column header into a given name and the rest of the name.
 * `line-clamp` inside the header button stays on one line (buttons do not
 * break at spaces), so each part is its own block. A closed-day label such
 * as "Fri" is a separate row, not the surname.
 */
export function practitionerColumnNameLines(name: string): {
  line1: string;
  line2: string | null;
} {
  const normalized = name.trim().replace(/\s+/g, ' ');
  const splitAt = normalized.indexOf(' ');
  if (splitAt === -1) return { line1: normalized, line2: null };
  const line2 = normalized.slice(splitAt + 1).trim();
  if (!line2) return { line1: normalized, line2: null };
  return { line1: normalized.slice(0, splitAt), line2 };
}

export type CalendarDayHours = {
  enabled: boolean;
  start: string;
  end: string;
  periods?: Array<{ start: string; end: string }>;
  source?: 'weekly' | 'exception';
  exceptionType?: string | null;
};

export function parseHourFromHhmm(value: string): number {
  const [h, m] = value.split(':').map(Number);
  if (!Number.isFinite(h)) return 0;
  return h + (Number.isFinite(m) ? m / 60 : 0);
}

/**
 * Day and week grids always cover 00:00–24:00 so closed time stays reachable.
 * Opening hours only choose the initial scroll (`calendarInitialScrollTop`).
 */
export function visibleCalendarHours(_input?: {
  businessHours?: Iterable<CalendarDayHours | null | undefined>;
  eventIsoTimes?: string[];
}): { startHour: number; endHour: number } {
  return { startHour: DEFAULT_DAY_START_HOUR, endHour: DEFAULT_DAY_END_HOUR };
}

/**
 * Referral occupancy grid only: opening hours plus one hour on each side.
 * Aftalebog must not use this — clipping the grid removes the 00:00–24:00 scroll.
 */
export function paddedOpeningCalendarHours(input?: {
  businessHours?: Iterable<CalendarDayHours | null | undefined>;
  eventIsoTimes?: string[];
}): { startHour: number; endHour: number } {
  void input?.eventIsoTimes;
  const range = input?.businessHours ? calendarOpeningHoursRange(input.businessHours) : null;
  if (!range) {
    return { startHour: DESIGN_DAY_START_HOUR, endHour: DESIGN_DAY_END_HOUR };
  }
  return {
    startHour: Math.max(DEFAULT_DAY_START_HOUR, Math.floor(range.start) - VISIBLE_HOURS_PAD),
    endHour: Math.min(DEFAULT_DAY_END_HOUR, Math.ceil(range.end) + VISIBLE_HOURS_PAD),
  };
}

function periodsOf(
  hours: CalendarDayHours | null | undefined,
): Array<{ start: string; end: string }> {
  if (!hours?.enabled) return [];
  if (hours.periods && hours.periods.length > 0) return hours.periods;
  return [{ start: hours.start, end: hours.end }];
}

function isClosedClinicException(hours: CalendarDayHours): boolean {
  return hours.exceptionType === 'closed';
}

/**
 * Grey-out window for one practitioner column.
 * Valid clinic hours ∩ practitioner hours. A weekly-closed or invalid clinic
 * day uses the practitioner's own hours, so Monday 09–17 stays open when the
 * clinic weekday is unchecked or ends before it starts. A dated clinic-wide
 * closed exception still greys the column. The 00–24 grid is unchanged.
 */
export function intersectCalendarDayHours(
  clinic: CalendarDayHours | null | undefined,
  practitioner: CalendarDayHours | null | undefined,
): CalendarDayHours | null {
  if (!clinic && !practitioner) return null;
  if (clinic && isClosedClinicException(clinic)) {
    return { ...clinic, periods: clinic.periods ?? [], enabled: false };
  }
  if (!clinic) return practitioner ?? null;

  const clinicPeriods = usableHourPeriods(periodsOf(clinic));
  const practitionerPeriods = practitioner ? usableHourPeriods(periodsOf(practitioner)) : [];
  if (!practitioner) {
    if (clinicPeriods.length === 0) {
      return { ...clinic, periods: clinic.periods ?? [], enabled: false };
    }
    return clinic;
  }

  const periods = combineClinicAndPractitionerPeriods(
    {
      enabled: clinicPeriods.length > 0,
      periods: clinicPeriods,
      source: clinic.source === 'exception' ? 'exception' : 'weekly',
      exceptionType: clinic.exceptionType === 'custom_hours' ? 'custom_hours' : null,
    },
    practitionerPeriods,
  );

  if (clinicPeriods.length === 0 || practitionerPeriods.length === 0) {
    if (periods.length === 0) {
      return { ...practitioner, periods: practitioner.periods ?? [], enabled: false };
    }
    return {
      ...practitioner,
      enabled: true,
      start: periods[0].start,
      end: periods[periods.length - 1].end,
      periods,
      exceptionType: practitioner.exceptionType ?? null,
    };
  }

  return {
    enabled: periods.length > 0,
    start: periods[0]?.start ?? clinic.start,
    end: periods[periods.length - 1]?.end ?? clinic.end,
    periods,
    source:
      clinic.source === 'exception' || practitioner.source === 'exception'
        ? 'exception'
        : (clinic.source ?? practitioner.source),
    exceptionType: clinic.exceptionType ?? practitioner.exceptionType ?? null,
  };
}

/** Earliest enabled open and latest enabled close from the given day hours. */
export function calendarOpeningHoursRange(
  hours: Iterable<CalendarDayHours | null | undefined>,
): { start: number; end: number } | null {
  let start = Number.POSITIVE_INFINITY;
  let end = Number.NEGATIVE_INFINITY;
  for (const day of hours) {
    if (!day?.enabled) continue;
    const periods =
      day.periods && day.periods.length > 0 ? day.periods : [{ start: day.start, end: day.end }];
    for (const period of periods) {
      const open = parseHourFromHhmm(period.start);
      const close = parseHourFromHhmm(period.end);
      if (!Number.isFinite(open) || !Number.isFinite(close) || close <= open) continue;
      start = Math.min(start, open);
      end = Math.max(end, close);
    }
  }
  if (!Number.isFinite(start) || !Number.isFinite(end)) return null;
  return { start, end };
}

export function calendarHoursMidpoint(range: { start: number; end: number } | null): number {
  if (!range) {
    return (DEFAULT_DAY_START_HOUR + DEFAULT_DAY_END_HOUR) / 2;
  }
  return range.start + (range.end - range.start) / 2;
}

export function calendarInitialScrollTop(input: {
  midpointHour: number;
  hourHeight: number;
  dayStartHour: number;
  dayEndHour: number;
  viewportHeight: number;
  stickyHeaderPx?: number;
}): number {
  const spanHours = input.dayEndHour - input.dayStartHour;
  const gridHeight = Math.max(0, spanHours * input.hourHeight);
  const headerPx = Math.max(0, input.stickyHeaderPx ?? 0);
  const visibleTimePx = Math.max(0, input.viewportHeight - headerPx);
  const midpointPx = (input.midpointHour - input.dayStartHour) * input.hourHeight;
  const maxScroll = Math.max(0, gridHeight + headerPx - input.viewportHeight);
  const raw = midpointPx - visibleTimePx / 2;
  return Math.min(maxScroll, Math.max(0, raw));
}

/** Grey bands for times patients cannot book online (or the clinic is closed). */
export function closedHourOverlays(input: {
  dayStartHour: number;
  dayEndHour: number;
  hourHeight: number;
  hours: CalendarDayHours | null | undefined;
}): Array<{ top: number; height: number }> {
  const span = input.dayEndHour - input.dayStartHour;
  const totalHeight = span * input.hourHeight;
  if (totalHeight <= 0) return [];

  if (!input.hours?.enabled) {
    return [{ top: 0, height: totalHeight }];
  }

  const periods =
    input.hours.periods && input.hours.periods.length > 0
      ? input.hours.periods
      : [{ start: input.hours.start, end: input.hours.end }];

  const overlays: Array<{ top: number; height: number }> = [];
  let cursor = input.dayStartHour;
  for (const period of periods) {
    const openStart = parseHourFromHhmm(period.start);
    const openEnd = parseHourFromHhmm(period.end);
    if (openStart > cursor) {
      overlays.push({
        top: (cursor - input.dayStartHour) * input.hourHeight,
        height: (openStart - cursor) * input.hourHeight,
      });
    }
    cursor = Math.max(cursor, openEnd);
  }
  if (cursor < input.dayEndHour) {
    overlays.push({
      top: (cursor - input.dayStartHour) * input.hourHeight,
      height: (input.dayEndHour - cursor) * input.hourHeight,
    });
  }

  return overlays.filter((overlay) => overlay.height > 0);
}

export function fitHourHeight(availablePx: number, hourCount: number): number {
  if (hourCount <= 0 || availablePx <= 0) return MAX_HOUR_HEIGHT;
  // Keep a readable row height so morning/evening hours stay reachable by scroll
  // instead of shrinking the whole day into the viewport.
  return MAX_HOUR_HEIGHT;
}

/** Which extra lines fit inside a timed calendar block without clipping glyphs. */
export function calendarBlockLineVisibility(heightPx: number): {
  showTime: boolean;
  showService: boolean;
} {
  return {
    showTime: heightPx >= 28,
    showService: heightPx > 44,
  };
}

/** Pixel offset of the red now-line, or null when the clock is outside the grid. */
export function calendarNowLineTop(input: {
  now: Date;
  dayStartHour: number;
  dayEndHour: number;
  hourHeight: number;
}): number | null {
  const hour = clinicMinutesFromMidnight(input.now) / 60;
  if (hour < input.dayStartHour || hour > input.dayEndHour) return null;
  return (hour - input.dayStartHour) * input.hourHeight;
}

export function isCalendarWeekend(date: Date): boolean {
  const weekday = new Intl.DateTimeFormat('en-US', {
    timeZone: 'Europe/Copenhagen',
    weekday: 'short',
  }).format(date);
  return weekday === 'Sat' || weekday === 'Sun';
}

/** Pixel position of a timed block on the shared time grid (week / resource). */
export function timedBlockGeometry(input: {
  startsAt: Date;
  endsAt: Date;
  hourHeight: number;
  dayStartHour: number;
  dayEndHour: number;
}): { top: number; height: number } | null {
  const startHour = clinicMinutesFromMidnight(input.startsAt) / 60;
  const endHour = clinicMinutesFromMidnight(input.endsAt) / 60;
  if (startHour >= input.dayEndHour) return null;
  if (endHour < input.dayStartHour) return null;

  const startMinutes = clinicMinutesFromMidnight(input.startsAt) - input.dayStartHour * 60;
  const durationMinutes = Math.max(
    15,
    (input.endsAt.getTime() - input.startsAt.getTime()) / 60_000,
  );
  return {
    top: Math.max(0, (startMinutes / 60) * input.hourHeight),
    height: Math.max(18, (durationMinutes / 60) * input.hourHeight - 2),
  };
}
